import { getArchetypes, getDefaultInputValues } from "@/features/branding";
import { decryptData, getSecretKey } from "@/features/cryptography";
import { UTF8_BOM } from "@/features/custom_downloads/utils";
import { store } from "@/store";
import { facadesUrl } from "@/store/services/facades";
import { supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { LargeSiteExportSummaryRequest } from "@/tools/risk-bands/large-site-screen/type";
import { KeyPairAndSalt, SecretSalt } from "@/types/cryptography";
import { hexToArrayBuffer } from "@/utils/crypto";
import { getUTCDate } from "@/utils/datetime";
import { unparse } from "@climaterisk/papaparse";
import * as turf from "@turf/turf";

export const getLargeSiteScreenUsageStream = (orgKey: KeyPairAndSalt, userKey: KeyPairAndSalt): ReadableStream => {
  let count = 0;
  const batchSize = 100;
  const timePeriod = { from: getUTCDate(2022, 1, 1), until: getUTCDate(new Date().getFullYear() + 1, 0, 1) };

  const encoder = new TextEncoder();

  return new ReadableStream({
    start: (controller) => {
      count = 0;

      controller.enqueue(UTF8_BOM);
      controller.enqueue(
        encoder.encode(
          [
            "Run Date",
            "User",
            "Group",
            "Analysis ID",
            "Analysis Name",
            "Requested",
            "Errored",
            "Analysed",
            "Date Deleted",
          ].join(",") + "\n"
        )
      );
    },
    pull: async (controller) => {
      const action = supabaseApi.endpoints.getLargeSites.initiate({
        dateFrom: timePeriod.from,
        dateUntil: timePeriod.until,
        page: count + 1,
        pageSize: batchSize,
      });

      const response = await dispatchWithRetries<{ data: any[]; count: number }>({
        action,
        maxRetries: 3,
        timeout: 5000,
      });
      count++;

      if (response === undefined || response.data === undefined || !response?.data.length) {
        controller.close();
        return;
      }
      // decrypt largeSite results
      const largeSites = await Promise.all(
        response.data.map(async (item) => {
          try {
            if (!item.secret_enc) throw Error;

            const secret = item.is_private
              ? await getSecretKey(userKey.keyPair.privateKey, item.secret_enc)
              : await getSecretKey(orgKey.keyPair.privateKey, item.secret_enc);
            const name = await decryptData(secret, hexToArrayBuffer(item.name || ""));

            return {
              ...item,
              key: item.id,
              name,
            };
          } catch {
            return {
              ...item,
              key: item.id,
              name: "[Encrypted Name]",
            };
          }
        })
      );

      let rows = "";
      largeSites.map((largeSite) => {
        const row = [
          largeSite.created_at,
          largeSite.email,
          largeSite.group_name,
          largeSite.id,
          largeSite.name,
          largeSite.total ?? 0,
          largeSite.error ?? 0,
          largeSite.success ?? 0,
          largeSite.deleted_at,
        ];
        rows += unparse([row]) + "\n";
      });
      controller.enqueue(encoder.encode(rows));

      if (response.data.length < batchSize) {
        controller.close();
      }
    },
  });
};

const preparePayload = async (
  portfolioId: string,
  secret: SecretSalt,
  stamps: string[],
  metadata: PortfolioExportJobSchema["metadata"]
) => {
  const action = supabaseApi.endpoints.getAllPortfolioAssetsEncrypted.initiate(portfolioId);
  const { data: encryptedAssets } = await store.dispatch(action);

  if (!encryptedAssets) {
    throw new Error("Failed to get assets for large site.");
  }

  if (!metadata?.riskiestPoint) {
    throw new Error("Unable to determine the riskiest point. Please contact support.");
  }

  if (!metadata?.polygons) {
    throw new Error("Failed to get polygons for large site.");
  }

  // Decrypt all encrypted UUIDs.
  const promises = encryptedAssets.map(async ({ data, encryptedId, id }) => {
    const decryptedData = data ? JSON.parse(await decryptData(secret, hexToArrayBuffer(data))) : null;
    const decryptedId = await decryptData(secret, hexToArrayBuffer(encryptedId));

    return {
      data: decryptedData,
      uuid: decryptedId,
      id,
    };
  });
  const assets = await Promise.all(promises);

  if (!assets.length) {
    throw new Error("Unable to decrypt assets for for large site. Please contact support.");
  }

  const selectedAssetData = assets[0].data;
  const totalArea = metadata.polygons.reduce<number>((total, polygon) => {
    return total + turf.area(polygon);
  }, 0);
  const archetypeName = getArchetypes().find((arch) => arch.value === selectedAssetData?.archetype)?.name;

  const settings: LargeSiteExportSummaryRequest["settings"] = {
    total_area: totalArea,
    floor_height: selectedAssetData?.thresholds?.flood?.height_above_ground,
    archetype: archetypeName,
    est_asset_market_value_whole: selectedAssetData?.financial?.market_value * assets.length,
    est_asset_market_value_by_point: selectedAssetData?.financial?.market_value,
    est_asset_replacement_cost_whole: selectedAssetData?.financial?.replacement_cost * assets.length,
    est_asset_replacement_cost_by_point: selectedAssetData?.financial?.replacement_cost,
    build_year: selectedAssetData?.build_year,
    riskiest_point: { year: metadata?.riskiestPoint.year, hazards: metadata?.riskiestPoint.hazard },
  };

  const body: LargeSiteExportSummaryRequest = {
    assets: assets.map(({ uuid, data }) => {
      const { marketValue, mortgageRate, replacementCost } = getDefaultInputValues(data?.archetype);
      return {
        uuid,
        mv: data.financial?.market_value ?? marketValue,
        rc: data.financial?.replacement_cost ?? replacementCost,
        mr: data.financial?.interest_rate ?? mortgageRate,
      };
    }),
    settings,
  };

  if (stamps.length) {
    body.stamps = stamps;
  }

  return body;
};

const createLargeSiteSummary = async (body: any): Promise<string> => {
  console.log("Creating large site summary");

  try {
    const res = await fetch(`${facadesUrl}large_site/request`, {
      method: "POST",
      headers: {
        Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw new Error(`Request failed with status: ${res.status}`);
    }

    const responseData = await res.json();
    const summaryId = responseData.report_id;

    if (!summaryId) {
      throw new Error("No report_id returned from request");
    }

    return summaryId;
  } catch (err) {
    throw {
      type: "failure",
      errors: [err],
    };
  }
};

export const enqueueLargeSiteSummary = async (
  portfolioId: string,
  previousSummaryId: string,
  secret: SecretSalt,
  stamps: string[],
  metadata: PortfolioExportJobSchema["metadata"]
): Promise<void> => {
  let summaryId = previousSummaryId;

  // Step 1: If no previous summary ID, enqueue portfolio
  // Step 1: If no previous summary ID, enqueue portfolio
  if (!previousSummaryId.length) {
    const body = await preparePayload(portfolioId, secret, stamps, metadata);
    summaryId = await createLargeSiteSummary(body);

    await store.dispatch(
      supabaseApi.endpoints.updatePortfolioReport.initiate({
        id: portfolioId,
        summaryId,
      })
    );
  }
};

export const downloadLargeSiteSummary = async (
  portfolioId: string,
  summaryId: string,
  secret: SecretSalt,
  stamps: string[],
  metadata: PortfolioExportJobSchema["metadata"],
  currency_code: string | undefined
) => {
  if (!summaryId) return;

  const body = await preparePayload(portfolioId, secret, stamps, metadata);

  console.log("body", body);

  const res = await fetch(`${facadesUrl}large_site/download`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
      Accept: "application/vnd.oasis.opendocument.spreadsheet",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      report_id: summaryId,
      settings: {
        ...body.settings,
        currency_code,
      },
      mode: "large_site",
    }),
  });

  if (!res.ok) {
    throw new Error("Large Site Summary not found");
  }

  return res.arrayBuffer();
};
